import {
  CancellationTypeCode,
  CartLeisureOrderStatus,
  CategoryType,
  ProductTypeCode,
  ReviewWritableResponseV2,
} from '~/__generated__/__types__'
import {
  CANCELLATION_STATUS,
  getServiceType,
  SERVICE_TYPE,
  STATUS,
} from '~/app/utils/OrderListUtils'
import { OrderListV3Query } from '~/__generated__/app/query/order-list/order-list.graphql'
import {
  ConvertedAPIFailedLeisureOrder,
  ConvertedOrder,
} from '~/app/components/order-converter/getCartOrderConverter'
import { isTypeOf } from '~/app/utils'
import { SharedOrderQuery } from '~/__generated__/app/query/order-shared/order-shared-list.graphql'
import { OrderDetailsCancellationCategoryOrders } from '~/app/components/order-list/hooks/useCancelInfo'
import { OrderCompleteV3Query } from '~/__generated__/app/query/order-complete/order-complete.graphql'
import conf from '~/app/conf'
import { DateTime } from 'luxon'
import { Category, Currency, IBrazePurchaseEventLog } from '~/app/braze/brazeCustomLog'
import {
  getBrazeOrderCategory,
  getOrderCompleteBrazeCouponAndPointInfo,
  IConverterBrazeCompositeOrderComplete,
} from '~/app/components/order-converter/useOrderCompleteBrazeCustomConverter'
import { CategoryTypeText } from '~/app/components/order-list/common/OrderCardItem'
import { ItemType } from '~/app/components/order-cancel/orderItems'
import yaLogo from '~/app/imgs/empty-thumnail-yanolja-h-56.svg?inline'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import { getFbqLeisureProductCheckInOutDate, IFbqPurchaseData } from '~/app/pixel'
import getReviewWritableButton from '~/app/utils/getReviewWritableButton'
import { CartOrderListPageLog } from '~/app/kinesis/protobuf/clientlogs/order-misc-common-txtpb'
import { getPromotionText } from '~/app/components/order-converter/converter/DomesticOrderConverter'
import {
  getBadgeTypeWithProductEvent,
  getPostOrderLeisureValidityPeriod,
} from '~/api/utils/leisure'
import { getDomesticVipBadgeType } from '~/api/utils/memberClass'
import {
  KinesisLogMetaId,
  OrderButton,
  OrderButtonIconType,
  OrderButtonType,
} from '~/app/components/order-converter/types'
import { LeisureWebApiVariantRefundApprovalTypeCode } from '~/api/services/leisure-web-api/index.type'
import { last } from 'lodash-es'
import { isAfterDateTime, isInPeriod } from '~/app/utils/datetime'
import { CancellationFeePlan } from '../../common/Leisure/types'

export const isLeisureCategoryType = (categoryType?: CategoryType) =>
  categoryType === CategoryType.DomesticLeisureRentacar ||
  categoryType === CategoryType.DomesticLeisureGiftcard ||
  categoryType === CategoryType.DomesticLeisureTicket

export const getLeisureStatus = (status: CartLeisureOrderStatus) =>
  status === CartLeisureOrderStatus.Used
    ? STATUS.COMPLETED
    : status === CartLeisureOrderStatus.CancelRequested
      ? STATUS.CANCEL_REQUESTED
      : status === CartLeisureOrderStatus.Unused
        ? STATUS.READY
        : STATUS.CANCEL_COMPLETED

export const getCancellationLeisureStatus = (status: CartLeisureOrderStatus) =>
  status === CartLeisureOrderStatus.ExpiredCancelCompleted ||
  status === CartLeisureOrderStatus.VendorCancelCompleted ||
  status === CartLeisureOrderStatus.UserCancelCompleted
    ? CANCELLATION_STATUS.COMPLETED
    : CANCELLATION_STATUS.IN_PROGRESS

export const getLeisureOptionTitle = ({
  variant,
  orderLength,
}: {
  variant: LeisureProductVariant
  orderLength?: number
}) => ({
  title: variant?.variantName || '',
  ...(orderLength && { caption: `${orderLength}장` }),
})

export const getLeisureInfo = ({
  valid: { from, to },
}: {
  valid: { from: string; to: string }
}) => {
  return [
    [
      getPostOrderLeisureValidityPeriod({
        validityStartDateTime: from,
        validityEndDateTime: to,
      }),
    ],
  ]
}

/**
 * 상품의 옵션그룹명이 전달되는 경우에만 노출
 *
 * @see {@link https://www.figma.com/design/Su1Qgs0p0zMiDgRecMj37u/UPD-1577-%5B%ED%86%B5%ED%95%A9%EC%A3%BC%EB%AC%B8%2F%EC%9E%85%EC%9E%A5%EA%B6%8C%5D-%ED%99%94%EB%8B%B4%EC%88%B2-%ED%94%84%EB%A1%9C%EC%A0%9D%ED%8A%B8?node-id=1278-12317&m=dev}
 */
export const getLeisureSubtitle = (
  item:
    | CartLeisureOrderItem
    | OrderDetailsLeisureItem
    | SharedOrderLeisureItem
    | OrderDetailsCancellationLeisureOrderItems[number]
) => {
  return item.variant?.productOptionGroup.productOptionGroupName ?? ''
}

export const getLeisureThumbnail = (thumbnailImageUrl?: string) => {
  return thumbnailImageUrl ?? yaLogo
}

export const isAfterLastEndDateTime = (plans: CancellationFeePlan[]) => {
  const lastPlan = last(plans)
  if (!lastPlan || !lastPlan.endDateTime) return false
  return isAfterDateTime(lastPlan.endDateTime)
}

/**
 * 현재 시각에 맞는 취소수수료 정책을 반환
 * 마지막 취소수수료 정책 기간이 지났다면 마지막 정책을 반환
 */
export const findPlanOrLast = (plans: CancellationFeePlan[]) =>
  isAfterLastEndDateTime(plans) ? last(plans)! : plans.find(plan => isInPeriod(plan))!

export const CANCELLATION_TEXT_TYPE = {
  /** 무료취소 */
  FREE: 'FREE',
  /** 무료취소 + 기간 */
  FREE_PERIOD: 'FREE_PERIOD',
  /** 취소수수료 */
  FEE: 'FEE',
  /** 취소수수료 + 비율 */
  FEE_RATIO: 'FEE_RATIO',
  /** 취소 및 환불불가 */
  NO_CANCELLATION_REFUND: 'NO_CANCELLATION_REFUND',
  /** 환불불가 */
  NO_REFUND: 'NO_REFUND',
} as const

export const getCancellationTextType = ({ typeCode, isNowInPeriod }) => {
  if (!isNowInPeriod) {
    return CANCELLATION_TEXT_TYPE.NO_CANCELLATION_REFUND
  }
  return {
    [CancellationTypeCode.Free]: () => CANCELLATION_TEXT_TYPE.FREE_PERIOD,
    [CancellationTypeCode.FullRefund]: () => CANCELLATION_TEXT_TYPE.FREE,
    [CancellationTypeCode.Fee]: () => CANCELLATION_TEXT_TYPE.FEE_RATIO,
    [CancellationTypeCode.FeeAutoRefund]: () => CANCELLATION_TEXT_TYPE.FEE_RATIO,
    [CancellationTypeCode.ManualFee]: () => CANCELLATION_TEXT_TYPE.FEE,
    [CancellationTypeCode.NoRefund]: () => CANCELLATION_TEXT_TYPE.NO_REFUND,
  }[typeCode]()
}

export type CartLeisureOrderItem = CartLeisureOrder['items'][number]
export type OrderDetailsLeisureItem = OrderDetailsLeisure['items'][number]
type LeisureProductVariant = CartLeisureOrderItem['variant'] | OrderDetailsLeisureItem['variant']
const isOrderDetailsLeisureItem = isTypeOf<OrderDetailsLeisureItem>(
  'OrderDetailsLeisureProductItem'
)

const getIsAvailableLeisureVoucher = (status: STATUS, isVoucherPageProvided: boolean): boolean => {
  return (status === STATUS.READY || status === STATUS.COMPLETED) && isVoucherPageProvided
}

export const isCancellable = (plans: CancellationFeePlan[]) => {
  const feePlan = findPlanOrLast(plans)
  const textType = getCancellationTextType({
    typeCode: feePlan.cancellationTypeCode,
    isNowInPeriod: isInPeriod({
      startDateTime: feePlan.startDateTime,
      endDateTime: feePlan.endDateTime,
    }),
  })

  return textType !== CANCELLATION_TEXT_TYPE.NO_CANCELLATION_REFUND
}

export const getLeisureButtons = ({
  props,
  categoryOrderId,
  reviewWritable,
  currentAt,
  kinesisLogMetaId,
}: {
  props: CartLeisureOrderItem | OrderDetailsLeisureItem
  categoryOrderId: string
  reviewWritable?: ReviewWritableResponseV2
  currentAt: string
  kinesisLogMetaId: KinesisLogMetaId
}) => {
  const { status, id, dealId, optionId, variant, encryptedVoucherData, product } = props
  const orderStatus = getLeisureStatus(status)
  const buttons: Array<OrderButton> = []

  if (orderStatus === STATUS.READY) {
    if (isCancellable(variant!.cancellationFeePlans)) {
      buttons.push({
        type: OrderButtonType.BASIC,
        title: '예약 취소',
        id: 'cancel',
        isDisabled: false,
        data: {
          id,
          categoryOrderId,
        },
      })
    }

    buttons.push({
      type: OrderButtonType.BASIC,
      title: '이용 정보',
      id: 'information',
      data: {
        type: 'leisure',
        id,
        dealId,
        optionId,
        categoryOrderId,
      },
    })
  }

  /**
   * 모바일 입장권 확인 페이지 제공
   * 미사용/이용완료 상태일 때 노출
   */
  if (getIsAvailableLeisureVoucher(orderStatus, variant!.isVoucherPageProvided)) {
    buttons.push({
      type: OrderButtonType.PRIMARY,
      title: '모바일 입장권 보기',
      id: 'voucher',
      icon: OrderButtonIconType.TICKET,
      data: {
        encrypted: encryptedVoucherData,
        kinesisLogMeta: {
          ...kinesisLogMetaId,
          productType: 'leisure',
          leisureTicketNo: +dealId,
          productGroupId: product?.productGroup?.productGroupId,
          dealOptionNo: +optionId,
        },
      },
    })
  }

  if (reviewWritable) {
    const reviewButton = getReviewWritableButton(reviewWritable, currentAt)
    if (reviewButton) {
      const kinesisLogMeta: CartOrderListPageLog.OrderProdInfoButtonWriteReviewBtn.Impression = {
        ...kinesisLogMetaId,
        productType: 'leisure',
        leisureTicketNo: dealId,
        productGroupId: String(product?.productGroup?.productGroupId),
        dealOptionNo: optionId,
      }
      buttons.push({
        ...reviewButton,
        data: {
          ...reviewButton.data,
          target: 'leisure',
          kinesisLogMeta: { ...kinesisLogMeta, activeYn: !reviewButton.isDisabled },
        },
      })
    }
  }

  return buttons
}

export const toLeisureOrder = ({
  props,
  categoryOrderId,
  categoryOrderType,
  reviewWritable,
  currentAt,
  kinesisLogMetaId,
  fromDetail,
}: {
  props: CartLeisureOrderItem | OrderDetailsLeisureItem
  categoryOrderId: string
  categoryOrderType: CategoryType
  reviewWritable?: ReviewWritableResponseV2
  currentAt: string
  kinesisLogMetaId: KinesisLogMetaId
  fromDetail: boolean
}): ConvertedOrder | ConvertedAPIFailedLeisureOrder => {
  const { product, variant, valid } = props

  const status = getLeisureStatus(props.status)
  const isLeisureCancelRequested = status === STATUS.CANCEL_REQUESTED

  if ((product && variant) || fromDetail) {
    const isLeisureCancelApproveByAdmin =
      variant!.refundApprovalTypeCode === LeisureWebApiVariantRefundApprovalTypeCode.ADMIN
    return {
      id: props.id,
      categoryOrderId,
      status,
      serviceType: SERVICE_TYPE.LEISURE,
      categoryType: categoryOrderType,
      title: props.dealName,
      imageSrc: getLeisureThumbnail(product!.productImage.thumbnailImageUrl),
      subTitle: getLeisureSubtitle(props),
      optionTitle: getLeisureOptionTitle({ variant }),
      info: getLeisureInfo({ valid }),
      buttons: getLeisureButtons({
        props,
        categoryOrderId,
        reviewWritable,
        currentAt,
        kinesisLogMetaId,
      }),
      price: isOrderDetailsLeisureItem(props) ? props.price : undefined,
      pdpUrl: `${conf.leisure.url}/leisure/${props.dealId}`,
      deeplink: `${conf.deeplink.scheme.leisure.detail}?idx=${props.dealId}`,
      hasPenalty: product!.isCancelPenalty,
      isRefundableAfterExpiration: variant!.isRefundableAfterExpiration,
      validityEndDateTime: variant!.validityEndDateTime,
      refundInfo: product?.productInfo?.refundInfo,
      useEndDate: isoToFormat(props.valid.to, 'yyyy-MM-dd'),
      isShowLeisureCancelRequestNotice: isLeisureCancelRequested && isLeisureCancelApproveByAdmin,
      promotion: isOrderDetailsLeisureItem(props) ? getPromotionText({ reviewWritable }) : '',
      cancellationFeePlans: variant!.cancellationFeePlans,
      vipMemberClassBadgeType: getDomesticVipBadgeType(
        isOrderDetailsLeisureItem(props) ? props.memberClass : undefined
      ),
      isAPIFailed: false,
    }
  } else {
    return {
      isAPIFailed: true,
      categoryOrderId,
      categoryType: categoryOrderType,
      title: props.dealName,
      imageSrc: getLeisureThumbnail(),
    }
  }
}

export type CartLeisureOrder = Extract<
  OrderListV3Query['orderListV3']['orders'][number]['categoryOrders'][number],
  { __typename?: 'CartLeisureProductOrder' }
>

export type OrderDetailsLeisure = Extract<
  OrderCompleteV3Query['orderDetailsV3']['categoryOrders'][number],
  { __typename?: 'OrderDetailsLeisureProduct' }
>

export const toCartLeisureOrder = (
  cartLeisureOrder: CartLeisureOrder,
  currentAt: string,
  kinesisLogMetaId: KinesisLogMetaId,
  fromDetail: boolean
) =>
  cartLeisureOrder.items.map(item =>
    toLeisureOrder({
      props: item,
      categoryOrderId: cartLeisureOrder.id,
      categoryOrderType: cartLeisureOrder.category,
      reviewWritable: cartLeisureOrder.reviewWritableV2,
      currentAt,
      kinesisLogMetaId,
      fromDetail,
    })
  )

export type SharedOrderLeisure = Extract<
  SharedOrderQuery['sharedOrder']['categoryOrders'][number],
  { __typename?: 'SharedOrderLeisure' }
>
type SharedOrderLeisureItem = SharedOrderLeisure['items'][number]
export const toSharedLeisureItem = (
  props: SharedOrderLeisureItem,
  categoryOrderId: string,
  categoryType: CategoryType
) => {
  const { dealName, product, id, variant, valid } = props

  return {
    categoryOrderId,
    serviceType: SERVICE_TYPE.LEISURE,
    categoryType,
    title: dealName,
    subTitle: getLeisureSubtitle(props),
    optionTitle: getLeisureOptionTitle({ variant }),
    info: getLeisureInfo({ valid }),
    imageSrc: product.productImage?.thumbnailImageUrl ?? yaLogo,
    id,
  }
}

export const toSharedLeisureOrder = (order: SharedOrderLeisure) =>
  order.items.map(item => toSharedLeisureItem(item, order.id, order.category))

export type OrderDetailsCancellationLeisureOrder = Extract<
  OrderDetailsCancellationCategoryOrders[number],
  { __typename?: 'OrderDetailsCancellationLeisureProduct' }
>
export type OrderDetailsCancellationLeisureOrderItems =
  OrderDetailsCancellationLeisureOrder['items']
interface IToCancellationLeisureOrder {
  items: OrderDetailsCancellationLeisureOrderItems
  categoryOrderId: string
  categoryType?: CategoryType
  currentAt: string
}
interface IToCancellationLeisureOrderItem {
  item: OrderDetailsCancellationLeisureOrderItems[number]
  categoryOrderId: string
  categoryType?: CategoryType
  currentAt: string
}

export const toCancellationLeisureOrder = ({
  items,
  categoryOrderId,
  categoryType,
  currentAt,
}: IToCancellationLeisureOrder) =>
  items.map(item => toCancellationLeisureItem({ item, categoryOrderId, categoryType, currentAt }))

export const toCancellationLeisureItem = ({
  item,
  categoryOrderId,
  categoryType,
}: IToCancellationLeisureOrderItem) => {
  const { id, dealName, status, price, commissionAmount, product, variant, valid } = item

  return {
    id,
    categoryOrderId,
    status: getCancellationLeisureStatus(status),
    serviceType: getServiceType(ItemType.LEISURE, categoryType),
    title: dealName,
    subTitle: getLeisureSubtitle(item),
    optionTitle: getLeisureOptionTitle({ variant }),
    info: getLeisureInfo({ valid }),
    imageSrc: getLeisureThumbnail(product.productImage.thumbnailImageUrl),
    refundPrice: price,
    commissionAmount: commissionAmount,
    isCancelPenalty: product.isCancelPenalty,
    categoryType,
    refundInfo: product.productInfo?.refundInfo,
    cancellationFeePlans: variant.cancellationFeePlans,
    vipMemberClassBadgeType: getDomesticVipBadgeType(variant.memberClassTypeCode ?? undefined),
  }
}

type OrderCompleteCategoryOrder = OrderCompleteV3Query['orderDetailsV3']['categoryOrders'][number]
type OrderCompleteLeisureItem = Extract<
  OrderCompleteCategoryOrder,
  { __typename?: 'OrderDetailsLeisureProduct' }
>['items'][number]
export const toOrderCompleteProductLeisure = ({
  orders,
  categoryOrderId,
  categoryOrderType,
  currentAt,
}: {
  orders: OrderCompleteLeisureItem[]
  categoryOrderId: string
  categoryOrderType: CategoryType
  currentAt: string
}) => {
  const { id, dealName, optionId, valid, product, variant } = orders[0]
  const badges = getBadgeTypeWithProductEvent({ productEvent: product.productEvent, currentAt })

  return {
    id,
    categoryOrderId,
    status: STATUS.READY, // 주문완료 화면에서 상태는 표현하지 않음
    serviceType: SERVICE_TYPE.LEISURE,
    title: dealName,
    subTitle: getLeisureSubtitle(orders[0]),
    optionTitle: getLeisureOptionTitle({
      orderLength: orders.length,
      variant,
    }),
    info: getLeisureInfo({ valid }),
    imageSrc: getLeisureThumbnail(product.productImage.thumbnailImageUrl),
    categoryType: categoryOrderType,
    badges,
    dealOptionId: optionId,
    valid: valid,
    hasPenalty: product.isCancelPenalty,
    isRefundableAfterExpiration: variant.isRefundableAfterExpiration,
    validityEndDateTime: variant.validityEndDateTime,
    refundInfo: product?.productInfo?.refundInfo,
    useEndDate: isoToFormat(valid.to, 'yyyy-MM-dd'),
    cancellationFeePlans: variant.cancellationFeePlans,
    vipMemberClassBadgeType: getDomesticVipBadgeType(variant.memberClassTypeCode ?? undefined),
  }
}

export const toOrderCompleteBrazeCustomLeisure = ({
  order,
  orderDetails,
  category,
  isNolPayment,
  currentAt,
}: {
  order: OrderDetailsLeisure
  orderDetails: OrderCompleteV3Query['orderDetailsV3']
  category: string
  isNolPayment: boolean
  currentAt: string
}): IConverterBrazeCompositeOrderComplete => {
  const { dealId, dealName, optionId, optionName, price, variant, product } = order.items[0]
  const { createdAt, id } = orderDetails
  const formatDate = date => DateTime.fromISO(date).toFormat('yyyy-MM-dd')
  const quantity = order.items.length
  const checkInOutDate = getFbqLeisureProductCheckInOutDate(variant, currentAt)
  const isSameDate = formatDate(createdAt) === formatDate(checkInOutDate.checkin_date)
  const isAfterDate =
    (DateTime.fromISO(checkInOutDate.checkin_date)
      .diff(DateTime.fromISO(createdAt), 'days')
      .toObject().days || 0) > 0

  return {
    category: getBrazeOrderCategory(order),
    composite_order_id: id,
    property_category: category,
    item_id: dealId,
    item_name: dealName,
    check_in_date: checkInOutDate.checkin_date,
    check_out_date: checkInOutDate.checkout_date,
    is_earlybird: !isSameDate && isAfterDate,
    item_detail_id: optionId.toString(),
    item_detail_name: optionName,
    cust_count: quantity,
    ...getOrderCompleteBrazeCouponAndPointInfo({
      orderDetails,
      coupon: order.leisureDiscount?.coupons[0],
      price: price * quantity,
    }),
    nolcard_approval: isNolPayment,
    city_name: product?.braze?.cityName ?? '',
  }
}

export const getOrderCompleteFacebookPixelLeisureData = ({
  order,
  currentAt,
}: {
  order: OrderDetailsLeisure
  currentAt: string
}): IFbqPurchaseData => {
  const { dealId, price, product, variant } = order.items[0]
  const quantity = order.items.length
  return {
    content_type: 'product',
    content_ids: `LE-${dealId}`,
    city: product.braze?.cityName ?? '',
    region: product.braze?.cityDetail ?? '',
    num_adults: quantity,
    num_children: 0,
    value: price * quantity,
    currency: 'KRW',
    ...getFbqLeisureProductCheckInOutDate(variant, currentAt),
  }
}

export const toReceiptLeisure = (
  props: OrderDetailsLeisureItem,
  categoryOrderId: string,
  categoryOrderType: CategoryType
): ConvertedOrder => ({
  id: props.id,
  status: getLeisureStatus(props.status),
  title: props.dealName,
  subTitle: getLeisureSubtitle(props),
  serviceType: CategoryTypeText[categoryOrderType],
  categoryOrderId,
  imageSrc: getLeisureThumbnail(props.product.productImage.thumbnailImageUrl),
  optionTitle: getLeisureOptionTitle({ variant: props.variant }),
  info: getLeisureInfo({ valid: props.valid }),
  categoryType: categoryOrderType,
  price: isOrderDetailsLeisureItem(props) ? props.price : undefined,
  isRefundableAfterExpiration: props.variant.isRefundableAfterExpiration,
  validityEndDateTime: props.variant.validityEndDateTime,
  refundInfo: props.product?.productInfo?.refundInfo,
  vipMemberClassBadgeType: getDomesticVipBadgeType(props.variant.memberClassTypeCode ?? undefined),
})

export const toOrderCompleteBrazePurchaseLeisure = (
  orders: OrderCompleteLeisureItem[],
  payments: OrderCompleteV3Query['orderDetailsV3']['payments'],
  paid_amount: number
): IBrazePurchaseEventLog => {
  const { dealId, product, price } = orders[0]
  const payment_method = payments.map(payment => payment?.paymentMethod?.code || '')

  return {
    productId: dealId,
    price,
    currencyCode: Currency.KRW,
    purchaseProperties: {
      category:
        product.productTypeCode === ProductTypeCode.Leisure ? Category.LEISURE : Category.GIFTCARD,
      payment_method,
      paid_amount,
    },
  }
}
